export const SEPARATE_BY_DASH = 'separate_by_dash';

/**
 * A redirect entry comes in one of 3 shapes, discriminated by which of
 * `replacement` / `name` is present:
 * - simple version: regex and replacement: `{ regex, replacement: '$1-$2...$N' }`
 *   (the `SEPARATE_BY_DASH` const is just a special `replacement` value)
 * - complex version: regex with `name` and `params`: `{ regex, name, params: {} }`
 * - query version: regex with `name` and `queryParams`: `{ regex, name, queryParams: {} }`
 */
interface ReplacementRedirectPage {
  regex: RegExp;
  replacement: string;
}

interface NamedRedirectPage {
  regex: RegExp;
  name: string;
  params: Record<string, string>;
}

interface QueryRedirectPage {
  regex: RegExp;
  name: string;
  queryParams: Record<string, string>;
}

export type StaticRedirectPage = ReplacementRedirectPage | NamedRedirectPage | QueryRedirectPage;

export const staticRedirectPages = {
  catalog: {
    regex: /^\/(catalog)\/?$/i,
    replacement: '$1',
  },
  faq: {
    regex: /^\/(faq)\/?$/i,
    replacement: '$1',
  },
  foodDeliveryMap: {
    regex: /^\/(food-delivery-map)\/?$/i,
    replacement: '$1',
  },
  inspirations: {
    regex: /^\/(inspirations)\/?$/i,
    replacement: '$1',
  },
  news: {
    regex: /^\/(news)\/?$/i,
    replacement: '$1',
  },
  newsTypeSlug: {
    regex: /^\/news\/([^/]+?)\/([^/]+?)\/?$/i,
    params: {
      type: '$1',
      slug: '$2',
    },
    name: 'news-type-slug',
  },
  press: {
    regex: /^\/(press)\/?$/i,
    replacement: '$1',
  },
  prizeGame: {
    regex: /^\/(prize-game)\/?$/i,
    replacement: '$1',
  },
  unsubscribe: {
    regex: /^\/(unsubscribe)\/?$/i,
    replacement: '$1',
  },
  // `/petrol` was the Hungarian URL before the URLs were localised; it is kept
  // alive for old bookmarks. The target is a literal path rather than a route
  // name because the landing is now a CMS page with no static route — if the
  // landing slug ever changes in the CMS, this has to change with it.
  petrol: {
    regex: /^\/(petrol)\/?$/i,
    replacement: '/benzinkutak',
  },
  petrolFaq: {
    regex: /^\/(petrol)\/(faq)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  petrolSlug: {
    regex: /^\/petrol\/([^/]+?)\/?$/i,
    params: {
      slug: '$1',
    },
    name: 'petrol-slug',
  },
  stores: {
    regex: /^\/(stores)\/?$/i,
    replacement: '$1',
  },
  storesSlug: {
    regex: /^\/stores\/([^/]+?)\/?$/i,
    params: {
      slug: '$1',
    },
    name: 'stores-slug',
  },
  loyaltyProgramFaq: {
    regex: /^\/(loyalty-program)\/(faq)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  orderAgain: {
    regex: /^\/(shop)\/(profile)\/(order-again)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  savedBaskets: {
    regex: /^\/(shop)\/(profile)\/(saved-baskets)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  orders: {
    regex: /^\/(shop)\/(profile)\/(orders)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  profileData: {
    regex: /^\/(shop)\/(profile)\/(data)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  profileManage: {
    regex: /^\/(shop)\/(profile)\/(manage)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  profileBilling: {
    regex: /^\/(shop)\/(profile)\/(billing-addresses)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  profileDelivery: {
    regex: /^\/(shop)\/(profile)\/(delivery-addresses)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  profileMailing: {
    regex: /^\/(shop)\/(profile)\/(mailing-addresses)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  loyaltyProgram: {
    regex: /^\/(shop)\/(profile)\/(loyalty-program)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  loyaltyCard: {
    regex: /^\/(shop)\/(profile)\/(loyalty-card)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  loyaltyCoupons: {
    regex: /^\/(shop)\/(profile)\/(loyalty-card)\/(coupons)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  loyaltyPartnerProgram: {
    regex: /^\/(shop)\/(profile)\/(loyalty-card)\/(partner-program)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  loyaltyTransactions: {
    regex: /^\/(shop)\/(profile)\/(loyalty-card)\/(transactions)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  loyaltyStickers: {
    regex: /^\/(shop)\/(profile)\/(loyalty-card)\/(stickers)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  loyaltyStickerById: {
    regex: /^\/shop\/profile\/loyalty-card\/stickers\/([^/]+?)\/?$/i,
    params: {
      id: '$1',
    },
    name: 'shop-profile-loyalty-card-stickers-id',
  },
  compare: {
    regex: /^\/(shop)\/(compare)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  basket: {
    regex: /^\/(shop)\/(basket)\/?$/i,
    replacement: SEPARATE_BY_DASH,
  },
  loyaltyOffers: {
    regex: /^\/(shop)\/(loyalty-offers)\/([^/]+)$/i,
    name: 'shop-loyalty-offers',
    queryParams: {
      list_name: '$3',
    },
  },
} as const satisfies Record<string, StaticRedirectPage>;
